import { zodResolver } from "@hookform/resolvers/zod";
import { Save } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import PageHeader from "../../../components/common/PageHeader";
import { Button, Card } from "../../../components";
import { useAuthSession } from "../../auth/shared";
import { ChangePasswordDialog } from "../components/ChangePasswordDialog";
import { ProfileForm } from "../components/ProfileForm";
import { useUpdateProfile } from "../hooks/useUpdateProfile";
import {
  createProfileFormSchema,
  toProfileFormDefaults,
  type ProfileFormValidationMessages,
  type ProfileFormValues,
} from "../schemas/profile.schema";

const PROFILE_FORM_ID = "profile-form";

const ProfilePage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuthSession();
  const { loading, error, success, updateProfile, reset } = useUpdateProfile();
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);

  const messages = useMemo<ProfileFormValidationMessages>(
    () => ({
      firstNameMinLength: t("profile.validation.first_name_min_length"),
      lastNameMinLength: t("profile.validation.last_name_min_length"),
      phoneNumberInvalid: t("profile.validation.phone_number_invalid"),
      avatarTooLarge: t("profile.validation.avatar_too_large"),
      avatarInvalidType: t("profile.validation.avatar_invalid_type"),
      genderInvalid: t("profile.validation.gender_invalid"),
    }),
    [t],
  );

  const form = useForm<ProfileFormValues>({
    mode: "onBlur",
    reValidateMode: "onChange",
    resolver: zodResolver(createProfileFormSchema(messages)),
    defaultValues: toProfileFormDefaults(user),
  });

  useEffect(() => {
    form.reset(toProfileFormDefaults(user));
  }, [form, user]);

  useEffect(() => {
    if (!success) {
      return;
    }

    const timeoutId = window.setTimeout(reset, 3000);
    return () => window.clearTimeout(timeoutId);
  }, [reset, success]);

  const handleSave = async (values: ProfileFormValues) => {
    if (!user) {
      return;
    }

    await updateProfile({
      firstName: values.firstName,
      lastName: values.lastName,
      phoneNumber: values.phoneNumber,
      profileImageFile: values.profileImageFile,
      gender: values.gender,
    });
  };

  const isBusy = loading;

  return (
    <Card className="p-6">
      <PageHeader
        title={t("profile.profile_settings")}
        actions={
          <Button
            type="submit"
            form={PROFILE_FORM_ID}
            disabled={isBusy}
            className="flex items-center gap-2"
          >
            <Save className="h-4 w-4" />
            {loading ? t("profile.saving") : t("common.save_changes")}
          </Button>
        }
      />

      {success && (
        <div className="mb-4 rounded-md border border-green-200 bg-green-50 p-3 text-green-800 shadow-sm dark:border-green-800 dark:bg-green-900/20 dark:text-green-300">
          Profile updated successfully!
        </div>
      )}

      {error && (
        <div className="mb-4 rounded border border-red-400 bg-red-100 p-3 text-red-700">
          {error}
        </div>
      )}

      <form
        id={PROFILE_FORM_ID}
        onSubmit={form.handleSubmit(handleSave)}
      >
        <ProfileForm
          form={form}
          user={user}
          disabled={isBusy}
          onChangePassword={() => setIsChangePasswordOpen(true)}
        />
      </form>

      <ChangePasswordDialog
        open={isChangePasswordOpen}
        onOpenChange={setIsChangePasswordOpen}
      />
    </Card>
  );
};

export default ProfilePage;
